import * as React from "react";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { ValidatorContents } from "../../../../../../Dashboard/Common/ValidatorHelper";
import {
    Numeric2,
    Numeric8AtLeastOneNonZero,
    AlphanumericWithSpace,
    AlphanumericWithSpecialCharacters,
} from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";

interface INewBankAccountProps extends IProcessComponentProps {
    isReview?: boolean;
    data: BankAccount;
    hasDefault: boolean;
    onChange: (changes) => void;
    onClickSave: VoidFunction;
    onClickCancel: VoidFunction;
}

interface INewBankAccountState {
    SortCode: string[];
    NumericSortCode: number;
}

export class NewBankAccount extends ProcessComponent<INewBankAccountProps, INewBankAccountState> {
    constructor(props: any) {
        super(props);
        this.state = {
            SortCode:
                this.props.data && this.props.data.SortCode
                    ? this.props.data.SortCode.toString().match(/.{1,2}/g)
                    : [null, null, null],
            NumericSortCode: this.props.data && this.props.data.SortCode ? parseInt(this.props.data.SortCode) : 0,
        };
    }

    private onSave() {
        const flag = this.isValid();
        if (flag && !(this.props.data.IsDefault && this.props.hasDefault)) {this.props.onClickSave();}
    }

    render() {
        const { data, isReview } = this.props;
        return (
            <>
                <TextBoxEditor
                    disabled={isReview}
                    label="Account reference"
                    required="Please enter account reference"
                    value={data.AccountReference}
                    maxLength={50}
                    regex={{ errorMessage: ValidatorContents.AlphaNumericWithSpace, value: AlphanumericWithSpace }}
                    onChange={(v) => this.props.onChange({ AccountReference: v })}
                />
                <TextBoxEditor
                    disabled={isReview}
                    label="Account name"
                    required="Please enter account name"
                    value={data.AccountName}
                    maxLength={45}
                    regex={{
                        errorMessage: ValidatorContents.AlphaNumericWithSpecialCharacters,
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    onChange={(v) => this.props.onChange({ AccountName: v })}
                />

                <TextBoxEditor
                    disabled={isReview}
                    label="Bank name"
                    required="Please enter bank name"
                    value={data.BankDetails.Name}
                    maxLength={50}
                    regex={{
                        errorMessage: ValidatorContents.AlphaNumericWithSpecialCharacters,
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    onChange={(v) => this.props.onChange({ BankDetails: { Name: v } })}
                />
                {this.renderSortCodeEditor()}
                <TextBoxEditor
                    disabled={isReview}
                    label="Account number"
                    required="Please enter account number"
                    value={data.AccountNumber}
                    regex={{ errorMessage: ValidatorContents.NonZeroAccountNumber, value: Numeric8AtLeastOneNonZero }}
                    onChange={(v) => this.props.onChange({ AccountNumber: v })}
                />
                <CheckboxGroup
                    type="block"
                    options={[
                        {
                            label: "is default",
                            value: data.IsDefault,
                            validationRules: [
                                {
                                    errorMessage: "Can only mark one bank account as default",
                                    validator: (v) => {
                                        return !(v && this.props.hasDefault);
                                    },
                                },
                            ],
                            disabled: isReview,
                            onChange: (v) => this.props.onChange({ IsDefault: v }),
                        },
                    ]}
                />
                <div className="row vspace-top-24">
                    <div className="col-auto">
                        <button className="btn secondary" onClick={() => this.onSave()} disabled={this.props.isReview}>
                            Save bank account
                        </button>
                    </div>
                    <div className="col-auto">
                        <button className="btn secondary" onClick={this.props.onClickCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </>
        );
    }

    renderSortCodeEditor() {
        const { isReview } = this.props;
        const { SortCode, NumericSortCode } = this.state;

        return (
            <fieldset>
                <legend className="control-label">Sort code:*</legend>
                <div className="row narrow-gutter">
                    <div className="col-auto" style={{maxWidth:'75px'}}>
                        <TextBoxEditor
                            disabled={isReview}
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={SortCode[0]}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            validationRules={[
                                {
                                    errorMessage: ValidatorContents.NonZeroSortCode,
                                    validator: (v) => {
                                        return parseInt(v) + NumericSortCode !== 0;
                                    },
                                },
                            ]}
                            onChange={(v) => {
                                if (v) {
                                    this.onSortCodeChanges(0, v);
                                }
                            }}
                        />
                    </div>
                    <div className="col-auto" style={{maxWidth:'75px'}}>
                        <TextBoxEditor
                            disabled={isReview}
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={SortCode[1]}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            onChange={(v) => {
                                if (v) {
                                    this.onSortCodeChanges(1, v);
                                }
                            }}
                        />
                    </div>
                    <div className="col-auto" style={{maxWidth:'75px'}}>
                        <TextBoxEditor
                            disabled={isReview}
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={SortCode[2]}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            onChange={(v) => {
                                if (v) {
                                    this.onSortCodeChanges(2, v);
                                }
                            }}
                        />
                    </div>
                </div>
            </fieldset>
        );
    }

    onSortCodeChanges(index: number, value: string) {
        const _sortCode = this.state.SortCode.concat();
        _sortCode[index] = value;
        let result = "";
        _sortCode.forEach((ele) => {
            if (!ele) {ele = "";}
            result += ele;
        });
        const numericSortCode = parseInt(result);
        this.setState({ SortCode: _sortCode, NumericSortCode: numericSortCode });
        this.props.onChange({ SortCode: result });
    }
}
